import { useEffect, useState } from 'react';
import { Download, IndianRupee, Loader2, Receipt, Landmark } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const authHeaders = () => {
  const t = localStorage.getItem('sip_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};
const inr = (v) => `₹${Number(v || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const monthLabel = (m) => {
  const [y, mo] = m.split('-').map(Number);
  return new Date(y, mo - 1, 1).toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
};

function Stat({ label, value, testId, accent }) {
  return (
    <div className="ae-surface p-4" data-testid={testId}>
      <div className="ae-eyebrow">{label}</div>
      <div className={`mt-1.5 text-xl font-heading font-extrabold ${accent || ''}`}>{value}</div>
    </div>
  );
}

export default function SuperAdminGstPage() {
  const [data, setData] = useState(null);
  const [month, setMonth] = useState('');
  const [loading, setLoading] = useState(true);

  const load = async (m) => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/super-admin/gst-report${m ? `?month=${m}` : ''}`, { headers: authHeaders() });
      if (!res.ok) throw new Error(`Failed (${res.status})`);
      const d = await res.json();
      setData(d);
      if (!m && d.months?.length) {
        setMonth(d.months[0].month);
        load(d.months[0].month);
        return;
      }
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(''); }, []);

  const download = async (path, filename) => {
    const res = await fetch(`${API_URL}/api/super-admin/${path}?month=${month}`, { headers: authHeaders() });
    if (!res.ok) { toast.error('Export failed'); return; }
    const blob = await res.blob();
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = filename;
    a.click();
    URL.revokeObjectURL(a.href);
  };
  const downloadCsv = () => download('gst-report.csv', `ActanonEdu-GST-${month}.csv`);
  const downloadGstr1 = () => download('gstr1.json', `GSTR1_${month.slice(5, 7)}${month.slice(0, 4)}_${data?.sellerGstin || ''}.json`);

  const cur = data?.months?.find((m) => m.month === month);

  return (
    <div className="space-y-6" data-testid="gst-revenue-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="ae-eyebrow text-ae-blue">Super Admin · Finance</div>
          <h1 className="mt-1 text-2xl md:text-3xl font-heading font-extrabold tracking-tight">GST &amp; Revenue</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Individual plan sales by month, split by CGST, SGST and IGST{data?.sellerGstin ? ` · GSTIN ${data.sellerGstin}` : ''}.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {data?.months?.length > 0 && <select
            value={month}
            onChange={(e) => { setMonth(e.target.value); load(e.target.value); }}
            data-testid="gst-month-select"
            className="h-10 px-3 text-sm rounded-xl border border-input bg-white dark:bg-slate-900"
          >
            {(data?.months || []).map((m) => <option key={m.month} value={m.month}>{monthLabel(m.month)}</option>)}
          </select>}
          <Button onClick={downloadCsv} disabled={!month} variant="outline" className="gap-1.5" data-testid="gst-export-csv">
            <Download className="h-4 w-4" /> Export CSV
          </Button>
          <Button onClick={downloadGstr1} disabled={!month} className="gap-1.5" data-testid="gst-export-gstr1" title="GSTR-1 JSON for the GST offline tool / portal upload">
            <Download className="h-4 w-4" /> GSTR-1 JSON
          </Button>
        </div>
      </div>

      {loading && !data ? (
        <div className="py-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
      ) : !data?.months?.length ? (
        <div className="ae-surface p-10 text-center text-sm text-muted-foreground" data-testid="gst-empty">
          <Receipt className="h-8 w-8 mx-auto mb-2 text-ae-slate" /> No paid invoices yet.
        </div>
      ) : (
        <>
          {cur && (
            <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-3" data-testid="gst-month-summary">
              <Stat label="Invoices" value={`${cur.invoices}${cur.b2b ? ` · ${cur.b2b} B2B` : ''}`} testId="gst-stat-invoices" />
              <Stat label="Gross collected" value={inr(cur.gross)} testId="gst-stat-gross" accent="text-ae-blue" />
              <Stat label="Taxable value" value={inr(cur.taxable)} testId="gst-stat-taxable" />
              <Stat label="CGST" value={inr(cur.cgst)} testId="gst-stat-cgst" />
              <Stat label="SGST" value={inr(cur.sgst)} testId="gst-stat-sgst" />
              <Stat label="IGST" value={inr(cur.igst)} testId="gst-stat-igst" />
              <Stat label="Total GST" value={inr(cur.totalGst)} testId="gst-stat-total" accent="text-ae-success" />
              {cur.unassigned > 0 && <Stat label="GST · state not captured" value={inr(cur.unassigned)} testId="gst-stat-unassigned" accent="text-amber-600" />}
            </div>
          )}
          {cur?.unassigned > 0 && (
            <div className="rounded-xl border border-ae-warning/30 bg-ae-warning/[0.07] px-4 py-3 text-xs" data-testid="gst-unassigned-note">
              {inr(cur.unassigned)} of GST is from older invoices issued before buyer state was captured — assign it manually when filing.
            </div>
          )}

          <p className="text-[11px] text-muted-foreground" data-testid="gstr1-help">
            GSTR-1 JSON covers B2B (Table 4), B2C small (Table 7), HSN/SAC summary split B2B/B2C (Table 12) and documents issued (Table 13).
            Upload it on the GST portal via Returns → GSTR-1 → Prepare Offline → Upload, then review before filing.
          </p>
          <div className="ae-surface overflow-x-auto" data-testid="gst-invoice-table">
            <table className="w-full text-xs">
              <thead className="bg-ae-bg dark:bg-slate-800/50 text-left">
                <tr>{['Invoice', 'Date', 'Plan', 'Buyer', 'Place of supply', 'Taxable', 'CGST', 'SGST', 'IGST', 'Total'].map((h) => (
                  <th key={h} className="px-3 py-2.5 ae-eyebrow whitespace-nowrap">{h}</th>
                ))}</tr>
              </thead>
              <tbody className="divide-y divide-slate-200/80 dark:divide-slate-800">
                {(data.invoices || []).map((r) => (
                  <tr key={r.paymentId || r.invoiceNo} data-testid={`gst-row-${r.paymentId}`}>
                    <td className="px-3 py-2.5 font-mono whitespace-nowrap">{r.invoiceNo}</td>
                    <td className="px-3 py-2.5 whitespace-nowrap">{r.date}</td>
                    <td className="px-3 py-2.5">{r.plan}</td>
                    <td className="px-3 py-2.5">{r.buyerGstin ? <span>{r.buyerBusinessName}<br /><span className="font-mono text-muted-foreground">{r.buyerGstin}</span></span> : 'Consumer (B2C)'}</td>
                    <td className="px-3 py-2.5">{r.placeOfSupply}</td>
                    <td className="px-3 py-2.5 text-right">{inr(r.taxable)}</td>
                    <td className="px-3 py-2.5 text-right">{inr(r.cgst)}</td>
                    <td className="px-3 py-2.5 text-right">{inr(r.sgst)}</td>
                    <td className="px-3 py-2.5 text-right">{inr(r.igst)}</td>
                    <td className="px-3 py-2.5 text-right font-semibold">{inr(r.total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="ae-surface p-4" data-testid="gst-month-history">
            <div className="flex items-center gap-2 mb-3"><Landmark className="h-4 w-4 text-ae-blue" /><span className="text-sm font-semibold">All months</span></div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {data.months.map((m) => (
                <button key={m.month} type="button" onClick={() => { setMonth(m.month); load(m.month); }}
                  className={`text-left rounded-xl border px-3 py-2.5 text-xs transition-colors ${m.month === month ? 'border-ae-blue bg-ae-blue/5' : 'border-slate-200/80 dark:border-slate-800 hover:border-ae-blue/40'}`}
                  data-testid={`gst-month-${m.month}`}>
                  <div className="font-semibold flex items-center gap-1"><IndianRupee className="h-3 w-3" />{monthLabel(m.month)}</div>
                  <div className="text-muted-foreground">{m.invoices} invoices · GST {inr(m.totalGst)}</div>
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
